import { ChangeDetectorRef, Component, forwardRef, inject, input, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cn } from './cn';

/**
 *   <hlm-radio-group [(value)]="pick">
 *     <hlm-radio value="a">Option A</hlm-radio>
 *     <hlm-radio value="b">Option B</hlm-radio>
 *   </hlm-radio-group>
 */
@Component({
  selector: 'hlm-radio-group',
  standalone: true,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HlmRadioGroupComponent), multi: true },
  ],
  template: '<ng-content />',
  host: {
    role: 'radiogroup',
    '[class]': "_rootClass()",
  },
})
export class HlmRadioGroupComponent implements ControlValueAccessor {
  readonly value = model<unknown>(null);
  readonly disabled = model(false);
  readonly orientation = input<'vertical' | 'horizontal'>('vertical');
  readonly userClass = input<string>('', { alias: 'class' });

  private cdr = inject(ChangeDetectorRef);
  private onChange: (v: unknown) => void = () => {};
  onTouched: () => void = () => {};

  protected _rootClass = () =>
    cn(
      'flex',
      this.orientation() === 'horizontal' ? 'flex-row flex-wrap gap-5' : 'flex-col gap-2',
      this.userClass(),
    );

  select(v: unknown): void {
    if (this.disabled()) return;
    this.value.set(v);
    this.onChange(v);
    this.onTouched();
  }

  writeValue(v: unknown): void { this.value.set(v); this.cdr.markForCheck(); }
  registerOnChange(fn: (v: unknown) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled.set(d); }
}

@Component({
  selector: 'hlm-radio',
  standalone: true,
  template: `
    <label [class]="_rootClass()" (click)="select()">
      <span [class]="_dotClass()">
        <!-- Inner dot is always in the DOM; opacity-flip avoids the sub-pixel
             height shift that occurs when @if removes/re-inserts the element
             (selected radios were rendering 0.75px taller than unselected,
             which propagated up and made the dialog body wobble). -->
        <span
          class="h-2 w-2 rounded-full bg-primary-foreground transition-opacity"
          [class.opacity-0]="!selected()"
        ></span>
      </span>
      <span class="flex-1 text-sm text-foreground">
        @if (label()) { {{ label() }} }
        <ng-content />
      </span>
    </label>
  `,
})
export class HlmRadioComponent {
  readonly value = input.required<unknown>();
  readonly label = input<string | null>(null);

  private readonly group = inject(HlmRadioGroupComponent);

  protected selected = () => this.group.value() === this.value();

  protected _rootClass = () =>
    cn(
      'inline-flex items-center gap-2.5 cursor-pointer select-none',
      this.group.disabled() && 'opacity-60 cursor-not-allowed',
    );

  protected _dotClass = () =>
    cn(
      'grid h-4 w-4 flex-none place-items-center rounded-full border transition-colors',
      this.selected() ? 'border-primary bg-primary' : 'border-border bg-background hover:border-foreground/40',
    );

  select(): void {
    this.group.select(this.value());
  }
}
